import {
  fetch_company_cases,
  fetch_coordinator_assigned_company_cases,
} from '@/views/x-verdict_views/CompanyAdmin/CompanyCases/company_cases_live_queue';
import {
  create_requestor_application_note,
  fetch_requestor_application_notes,
} from '@/services/requestor_portal_service';
import {
  normalize_key,
  normalize_text,
  read_session_current_user_id,
  read_session_project_id,
} from '@/views/x-verdict_views/CompanyAdmin/CompanyCases/company_cases_common';
import {
  case_detail_query,
  due_at_date,
  group_count,
  inbox_item_matches_group,
  is_this_week,
  is_today,
  map_case_to_inbox_item,
  map_note_to_mention_item,
  note_authored_by_current_user,
  note_mentions_current_user,
  workflow_status_groups,
} from './company_inbox_helpers';
import {
  INBOX_GROUPS,
  TASK_GROUPS,
} from './company_inbox_constants';

const clone = (value) => JSON.parse(JSON.stringify(value));

const COMPANY_INBOX_STATE = {
  active_group: 'all',
  inbox_groups: INBOX_GROUPS,
  task_groups: TASK_GROUPS,
  inbox_items: [],
  mention_items: [],
  replying_mention_id: '',
  mention_reply_body: '',
  submitting_mention_reply: false,
  loading_inbox: false,
  loading_mentions: false,
  inbox_error: '',
};

function create_company_inbox_state() {
  return clone(COMPANY_INBOX_STATE);
}

function case_assignment_user_ids(case_item = {}) {
  return (Array.isArray(case_item?.assignment_user_ids) ? case_item.assignment_user_ids : [])
    .map((user_id) => Number(user_id))
    .filter((user_id) => Number.isFinite(user_id) && user_id > 0);
}

function is_case_owner_role(role = '') {
  const role_key = normalize_key(role);
  return role_key.includes('case-owner') || role_key === 'owner' || role_key.endsWith('-owner');
}

function is_requestor_role(role = '') {
  const role_key = normalize_key(role);
  return role_key.includes('requestor')
    || role_key.includes('requester')
    || role_key === 'entity';
}

function is_coordinator_role(role = '') {
  const role_key = normalize_key(role);
  return role_key.includes('coordinator') || role_key === 'coord';
}

function current_user_email() {
  if (typeof window === 'undefined') return '';
  return normalize_text(
    window.sessionStorage.getItem('user_email')
    || window.sessionStorage.getItem('email')
    || window.sessionStorage.getItem('username')
  ).toLowerCase();
}

function current_user_id(component) {
  return Number(
    component.$store?.getters?.currentUserId
    || sessionStorage.getItem('current_user_id')
    || sessionStorage.getItem('user_id')
    || 0
  ) || null;
}

function case_requestor_email(case_item = {}) {
  return normalize_text(
    case_item?.user_email
    || case_item?.requestor_email
    || case_item?.requester_email
    || case_item?.raw_record?.user_email
    || case_item?.raw_record?.requestor_email
    || case_item?.raw_record?.requester_email
  ).toLowerCase();
}

function assignment_user_id(assignment = {}) {
  return Number(
    assignment?.assigned_to_id
    || assignment?.user_id
    || assignment?.assigned_to?.id
    || 0
  ) || null;
}

function assignment_name(assignment = {}) {
  return normalize_text(
    assignment?.assigned_to_name
    || assignment?.name
    || assignment?.user_name
    || assignment?.assigned_to?.full_name
    || [assignment?.assigned_to?.first_name, assignment?.assigned_to?.last_name].filter(Boolean).join(' ')
  );
}

function assignment_email(assignment = {}) {
  return normalize_text(
    assignment?.assigned_to_email
    || assignment?.email
    || assignment?.assigned_to?.email
  );
}

function session_value(key = '') {
  if (typeof window === 'undefined') return '';
  return normalize_text(window.sessionStorage.getItem(key));
}

function current_user_global_tokens(component) {
  const email = current_user_email();
  const email_name = email.includes('@') ? email.split('@')[0] : '';

  return [
    component.$store?.getters?.currentLeadName,
    session_value('current_lead_name'),
    session_value('user_full_name'),
    session_value('full_name'),
    email,
    email_name,
  ];
}

function mention_token_variants(value = '') {
  const token = normalize_text(value);
  if (!token) {
    return [];
  }

  const spaced_token = token.replace(/[._-]+/g, ' ');
  const parts = spaced_token.split(/\s+/).filter((part) => part.length > 1);

  return [
    token,
    spaced_token,
    ...parts,
  ];
}

function current_user_case_tokens(case_item = {}) {
  const current_user_id = read_session_current_user_id();
  if (!current_user_id) {
    return [];
  }

  const assignments = Array.isArray(case_item?.application_assigned_users)
    ? case_item.application_assigned_users
    : [];

  return assignments
    .filter((assignment) => Number(assignment_user_id(assignment)) === Number(current_user_id))
    .flatMap((assignment) => [assignment_name(assignment), assignment_email(assignment)]);
}

function current_user_mention_tokens(component, case_item = {}) {
  const tokens = [
    ...current_user_global_tokens(component),
    ...current_user_case_tokens(case_item),
  ]
    .flatMap(mention_token_variants)
    .filter((token) => token.length > 1);

  return [...new Set(tokens.map((token) => token.toLowerCase()))];
}

function case_lookup_key(case_item = {}) {
  return String(
    case_item?.application_id
    || case_item?.entity_profile_id
    || case_item?.case_code
    || ''
  );
}

function unique_cases(cases = []) {
  const seen = new Set();

  return cases.filter((case_item) => {
    const key = case_lookup_key(case_item);
    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

export { current_user_id, current_user_mention_tokens, unique_cases };

function today_iso_date() {
  return new Date().toISOString().slice(0, 10);
}

function reply_body(item = {}, body = '') {
  const recipient = normalize_text(item.reply_to_name);
  const message = normalize_text(body);
  return recipient ? `[ReplyTo:${recipient}] @${recipient} ${message}` : message;
}

export function visible_cases_for_role(cases = [], role = '') {
  const current_user_id = read_session_current_user_id();

  if (is_case_owner_role(role)) {
    return current_user_id
      ? cases.filter((case_item) => case_assignment_user_ids(case_item).includes(current_user_id))
      : cases;
  }

  if (is_requestor_role(role)) {
    const email = current_user_email();
    return email
      ? cases.filter((case_item) => case_requestor_email(case_item) === email)
      : cases;
  }

  return cases;
}

export default {
  name: 'CompanyInbox',
  data: () => create_company_inbox_state(),
  created() {
    this.load_inbox();
  },
  computed: {
    project_id() {
      return this.$route?.query?.project_id
        || this.$route?.query?.projectId
        || read_session_project_id();
    },
    current_role() {
      return this.$store?.getters?.currentRole
        || sessionStorage.getItem('current_role')
        || sessionStorage.getItem('user_role')
        || '';
    },
    route_group() {
      return String(this.$route?.query?.status || this.$route?.query?.inbox_group || 'all')
        .trim()
        .toLowerCase();
    },
    display_inbox_groups() {
      return workflow_status_groups(this.inbox_items).map((group) => ({
        ...group,
        count: group.key === 'all'
          ? this.inbox_items.length + this.mention_items.length
          : group.key === 'mentions'
            ? this.mention_items.length
            : group_count(this.inbox_items, group.key),
      }));
    },
    display_task_groups() {
      return this.task_groups.map((task) => ({
        ...task,
        count: this.task_count(task.key),
      }));
    },
    filtered_items() {
      if (this.active_group === 'all') {
        return [...this.mention_items, ...this.inbox_items];
      }

      if (this.active_group === 'mentions') {
        return this.mention_items;
      }

      return this.inbox_items.filter((item) => inbox_item_matches_group(item, this.active_group));
    },
    selected_count_label() {
      return `${this.filtered_items.length} items`;
    }
  },
  watch: {
    route_group: {
      immediate: true,
      handler(group_key) {
        const matching_group = this.display_inbox_groups.find((group) => group.key === group_key);
        this.active_group = matching_group?.key || 'all';
      }
    },
    project_id() {
      this.load_inbox();
    },
    current_role() {
      this.load_inbox();
    }
  },
  methods: {
    async load_inbox() {
      this.loading_inbox = true;
      this.loading_mentions = false;
      this.inbox_error = '';

      try {
        const cases = await fetch_company_cases(this.project_id);
        const visible_cases = visible_cases_for_role(cases, this.current_role);
        const mention_cases = await this.mention_lookup_cases(cases);

        this.inbox_items = visible_cases.map(map_case_to_inbox_item);
        await this.load_mentions(mention_cases);
      } catch (error) {
        this.inbox_items = [];
        this.mention_items = [];
        this.inbox_error = error?.response?.data?.message || error?.message || 'Failed to load inbox.';
      } finally {
        this.loading_inbox = false;
      }
    },
    async mention_lookup_cases(cases = []) {
      if (!is_coordinator_role(this.current_role)) {
        return unique_cases(cases);
      }

      try {
        const assigned_cases = await fetch_coordinator_assigned_company_cases(this.project_id);
        return unique_cases([...cases, ...assigned_cases]);
      } catch {
        return unique_cases(cases);
      }
    },
    async load_mentions(cases = []) {
      this.loading_mentions = true;
      this.mention_items = [];

      try {
        const mention_lists = await Promise.all(cases.map(async (case_item) => {
          const application_id = Number(case_item?.application_id || 0);
          const mention_tokens = current_user_mention_tokens(this, case_item);
          const viewer_user_id = current_user_id(this);

          if (!application_id || !mention_tokens.length) {
            return [];
          }

          try {
            const notes = await fetch_requestor_application_notes(application_id);
            return notes
              .filter((note) => !note_authored_by_current_user(note, viewer_user_id, mention_tokens))
              .filter((note) => note_mentions_current_user(note, mention_tokens))
              .map((note, index) => map_note_to_mention_item(case_item, note, index));
          } catch {
            return [];
          }
        }));

        this.mention_items = mention_lists.flat();
      } finally {
        this.loading_mentions = false;
      }
    },
    set_active_group(group_key) {
      if (this.active_group === group_key) {
        return;
      }

      this.active_group = group_key;
      const next_query = { ...this.$route.query };

      delete next_query.inbox_group;

      if (!group_key || group_key === 'all') {
        delete next_query.status;
      } else {
        next_query.status = group_key;
      }

      this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
        query: next_query,
      });
    },
    task_count(task_key) {
      return this.inbox_items.filter((item) => {
        const due_at = due_at_date(item.case_item);
        return task_key === 'today' ? is_today(due_at) : is_this_week(due_at);
      }).length;
    },
    handle_inbox_action(item, action = {}) {
      if (action.opens_reply) {
        this.start_mention_reply(item);
        return;
      }

      if (action.opens_case) {
        this.go_to_case_detail(item);
      }
    },
    start_mention_reply(item = {}) {
      this.replying_mention_id = item.id;
      this.mention_reply_body = '';
      this.inbox_error = '';
    },
    cancel_mention_reply() {
      this.replying_mention_id = '';
      this.mention_reply_body = '';
    },
    async submit_mention_reply(item = {}) {
      const application_id = Number(item?.case_item?.application_id || 0);
      const user_id = current_user_id(this);
      const body = normalize_text(this.mention_reply_body);

      if (!application_id) {
        this.inbox_error = 'No case reference was found for this reply.';
        return;
      }

      if (!user_id) {
        this.inbox_error = 'No current user was found for this reply.';
        return;
      }

      if (!body) {
        this.inbox_error = 'Enter a reply before sending.';
        return;
      }

      this.submitting_mention_reply = true;
      this.inbox_error = '';

      try {
        await create_requestor_application_note({
          application_id,
          user_id,
          journal_description: reply_body(item, body),
          hours_spent: '0.00',
          journal_date: today_iso_date(),
        });
        this.cancel_mention_reply();
        await this.load_inbox();
      } catch (error) {
        this.inbox_error = error?.response?.data?.message || error?.message || 'Failed to send reply.';
      } finally {
        this.submitting_mention_reply = false;
      }
    },
    go_to_case_detail(item) {
      const case_item = typeof item === 'string' ? { case_code: item } : item?.case_item || item;
      const case_code = normalize_text(case_item?.case_code) || normalize_key(case_item?.title || 'case');

      this.$router.push({
        name: 'ViewCaseDetails',
        params: { caseCode: case_code },
        query: case_detail_query(case_item),
      });
    }
  }
};
